import { OtpCodeInput } from '@/components/shared/SupportEmailVerification';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  SECURITY_CHALLENGE_OTP_LEN,
  useSecurityChallenge,
} from '@/hooks/useSecurityChallenge';
import {
  SecurityChallengeMetadata,
  SecurityChallengePurpose,
} from '@/services/securityChallenge.service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type SecurityChallengeModalProps = {
  open: boolean;
  purpose: SecurityChallengePurpose;
  metadata?: SecurityChallengeMetadata;
  email?: string;
  onVerified: (challengeToken: string) => void;
  onCancel: () => void;
};

function onlyDigits(value: string) {
  return value.replace(/\D/g, '').slice(0, SECURITY_CHALLENGE_OTP_LEN);
}

function mapErrorKeyToMessage(
  key: string | null,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string | null {
  if (!key) return null;

  if (key.includes('metadata_mismatch')) {
    return t('common:securityChallengeMetadataMismatch');
  }
  if (key.includes('token_required')) {
    return t('common:securityChallengeTokenRequired');
  }
  if (key.includes('invalid_or_expired') || key.includes('expired')) {
    return t('common:securityChallengeInvalidOrExpiredToken');
  }
  if (key.includes('too_many') || key.includes('rate') || key.includes('throttle')) {
    return t('common:securityChallengeRateLimited');
  }
  if (key.includes('code')) {
    return t('common:securityChallengeInvalidCode');
  }

  // Mensagem já legível vinda da API tem prioridade sobre o fallback genérico.
  if (!key.includes('.') && key.trim().includes(' ')) {
    return key;
  }

  return t('common:securityChallengeGenericError');
}

export function SecurityChallengeModal({
  open,
  purpose,
  metadata,
  email,
  onVerified,
  onCancel,
}: SecurityChallengeModalProps) {
  const { t } = useTranslation(['common']);
  const [code, setCode] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const autoSentRef = useRef(false);

  const {
    sendCode,
    verifyCode,
    reset,
    resendCooldownSec,
    isSending,
    isVerifying,
    error,
  } = useSecurityChallenge({ purpose, metadata, onVerified });

  useEffect(() => {
    if (!open) {
      autoSentRef.current = false;
      setCode('');
      reset();
      return;
    }

    if (autoSentRef.current) return;
    autoSentRef.current = true;
    void sendCode().catch(() => undefined);
  }, [open, sendCode, reset]);

  useEffect(() => {
    if (!open) return;
    if (code.length !== SECURITY_CHALLENGE_OTP_LEN) return;
    if (isVerifying) return;

    void verifyCode(code).catch(() => {
      setCode('');
      window.requestAnimationFrame(() => inputRefs.current[0]?.focus());
    });
  }, [code, open, isVerifying, verifyCode]);

  const handleChange = useCallback(
    (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      const entered = onlyDigits(e.target.value);
      setCode((prev) => {
        const current = onlyDigits(prev);
        if (entered.length > 1) {
          return onlyDigits(current.slice(0, index) + entered);
        }
        const next = onlyDigits(
          current.slice(0, index) + (entered || '') + current.slice(index + 1),
        );
        return next;
      });

      if (entered) {
        const focusAt = Math.min(index + entered.length, SECURITY_CHALLENGE_OTP_LEN - 1);
        window.requestAnimationFrame(() => inputRefs.current[focusAt]?.focus());
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Backspace') return;
      if (e.currentTarget.value !== '' || index === 0) return;
      e.preventDefault();
      setCode((prev) => onlyDigits(prev).slice(0, index - 1));
      window.requestAnimationFrame(() => inputRefs.current[index - 1]?.focus());
    },
    [],
  );

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = onlyDigits(e.clipboardData.getData('text'));
    if (!pasted) return;
    setCode(pasted);
    const focusAt = Math.min(pasted.length, SECURITY_CHALLENGE_OTP_LEN - 1);
    window.requestAnimationFrame(() => inputRefs.current[focusAt]?.focus());
  }, []);

  const handleResend = useCallback(() => {
    if (resendCooldownSec > 0 || isSending) return;
    setCode('');
    void sendCode().catch(() => undefined);
  }, [resendCooldownSec, isSending, sendCode]);

  const errorMessage = useMemo(
    () => mapErrorKeyToMessage(error, t),
    [error, t],
  );

  const description = email
    ? t('common:securityChallengeDescriptionWithEmail', { email })
    : t('common:securityChallengeDescription');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent
        className="max-w-md border-colorBorder p-0 overflow-hidden"
        data-cy="security-challenge-dialog"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="p-6 pb-2 border-b border-colorBorder">
          <DialogTitle className="text-xl font-semibold">
            {t('common:securityChallengeTitle')}
          </DialogTitle>
          <DialogDescription className="text-left text-muted whitespace-pre-line">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-4 space-y-6">
          <OtpCodeInput
            length={SECURITY_CHALLENGE_OTP_LEN}
            digits={code.split('')}
            disabled={isVerifying}
            inputRefs={inputRefs}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            ariaLabel={t('common:securityChallengeEnterCode')}
            dataCyPrefix="security-challenge-otp"
          />

          {errorMessage && (
            <p
              className="text-sm text-error text-center"
              data-cy="security-challenge-error"
            >
              {errorMessage}
            </p>
          )}

          <div className="flex justify-center gap-6 flex-wrap">
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t('common:cancel')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleResend}
              disabled={isSending || isVerifying || resendCooldownSec > 0}
            >
              {isSending
                ? t('common:securityChallengeSending')
                : resendCooldownSec > 0
                  ? t('common:securityChallengeResendWait', {
                      seconds: resendCooldownSec,
                    })
                  : t('common:securityChallengeResend')}
            </Button>
          </div>

          {isVerifying && (
            <p className="text-sm text-muted text-center">
              {t('common:securityChallengeVerifying')}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
